import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ChevronDown,
  ChevronUp,
  Copy,
  FileText,
  Plus,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { z } from 'zod';
import type { ReportSection } from '~/convex/lib/types';

const SAVE_DEBOUNCE_MS = 1500;

const reportSectionSchema = z.object({
  title: z.string().min(1, 'report:validation.sectionTitleRequired'),
  content: z.string().min(1, 'report:validation.sectionContentRequired'),
});

export const reportFormSchema = z.object({
  generatedSections: z
    .array(reportSectionSchema)
    .min(1, 'report:validation.atLeastOneSectionRequired'),
});

export type ReportFormValues = z.infer<typeof reportFormSchema>;

interface ReportFormProps {
  defaultValues?: ReportFormValues;
  onCancel?: () => void;
  onSave: (args: { generatedSections: ReportSection[] }) => void;
  copyButton?: {
    onCopy: () => void;
    success: boolean;
    disabled: boolean;
  };
}

export const ReportForm = ({
  defaultValues,
  onSave,
  copyButton,
}: ReportFormProps) => {
  const { t } = useTranslation();

  const form = useForm<ReportFormValues>({
    resolver: zodResolver(reportFormSchema),
    defaultValues: defaultValues || {
      generatedSections: [],
    },
  });

  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: 'generatedSections',
  });

  const addSection = () => {
    append({ title: '', content: '' });
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;
    move(index, newIndex);
  };

  const debouncedSave = useDebouncedCallback(() => {
    const values = form.getValues();
    onSave({
      generatedSections: (values.generatedSections ?? []).map((s) => ({
        title: s?.title ?? '',
        content: s?.content ?? '',
      })),
    });
  }, SAVE_DEBOUNCE_MS);

  useEffect(() => {
    const subscription = form.watch(() => {
      debouncedSave();
    });
    return () => subscription.unsubscribe();
  }, [form, debouncedSave]);

  const onSubmit = useCallback(
    (data: ReportFormValues) => {
      onSave({
        generatedSections: (data.generatedSections ?? []).map((s) => ({
          title: s?.title ?? '',
          content: s?.content ?? '',
        })),
      });
    },
    [onSave],
  );

  return (
    <form
      className="flex w-full grow flex-col gap-6 pb-3 md:pb-0"
      onSubmit={form.handleSubmit(onSubmit)}>
      <Form {...form}>
        {/* Generated Sections */}
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="generatedSections"
            render={() => {
              return (
                <FormItem>
                  <div className="flex items-center justify-between gap-2 pb-1">
                    <FormLabel className="text-sm font-medium text-gray-700">
                      {t('report:sections')}
                    </FormLabel>
                    <div className="flex items-center gap-2">
                      {copyButton && (
                        <Button
                          type="button"
                          variant="outline"
                          size="md"
                          disabled={copyButton.disabled}
                          onClick={copyButton.onCopy}
                          aria-label={t('report:copyReport')}>
                          <Copy className="size-4" />
                          {copyButton.success
                            ? t('report:copyReportSuccess')
                            : t('report:copyReport')}
                        </Button>
                      )}
                      <Button
                        type="button"
                        onClick={addSection}
                        variant="outline"
                        size="md">
                        <Plus className="size-4" />
                        {t('report:addSection')}
                      </Button>
                    </div>
                  </div>
                  <FormControl>
                    {fields.length === 0 ? (
                      <div className="rounded-lg border border-dashed p-8 text-center">
                        <FileText className="mx-auto size-8 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-500">
                          {t('report:noSections')}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {fields.map((field, index) => (
                          <div
                            key={field.id}
                            className="rounded-lg border bg-gray-100 p-4">
                            <div className="flex flex-col gap-2">
                              <div className="flex-1 space-y-3">
                                {/* Section Title */}
                                <FormField
                                  control={form.control}
                                  name={`generatedSections.${index}.title`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel className="text-xs font-medium text-gray-600">
                                        {t('report:sectionTitle')}
                                      </FormLabel>
                                      <FormControl>
                                        <Input
                                          placeholder={t(
                                            'report:sectionTitlePlaceholder',
                                          )}
                                          className="bg-white shadow-none"
                                          {...field}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                {/* Section Content */}
                                <FormField
                                  control={form.control}
                                  name={`generatedSections.${index}.content`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel className="text-xs font-medium text-gray-600">
                                        {t('report:sectionContent')}
                                      </FormLabel>
                                      <FormControl>
                                        <Textarea
                                          placeholder={t(
                                            'report:sectionContentPlaceholder',
                                          )}
                                          className="bg-white shadow-none"
                                          rows={6}
                                          {...field}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                {/* Section Actions */}
                                <div className="flex justify-end gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={() => moveSection(index, 'up')}
                                    disabled={index === 0}>
                                    <ChevronUp className="size-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={() => moveSection(index, 'down')}
                                    disabled={index === fields.length - 1}>
                                    <ChevronDown className="size-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={() => remove(index)}>
                                    <Trash2 className="size-4 text-red-600" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
      </Form>
    </form>
  );
};
